import { HttpClient, HttpParams } from "@angular/common/http"
import { Injectable, inject } from "@angular/core"
import { Observable, filter, switchMap, take } from "rxjs"
import { environment } from "../../../environments/environment"
import { PageResponse } from "../../shared/components/generic-list/table-config"
import {
  DepreciationCalculationResult,
  DepreciationEntry,
  FixedAsset,
  FixedAssetCreate,
  FixedAssetUpdate,
} from "../models/asset.model"
import { AuthService } from "./auth.service"

@Injectable({
  providedIn: "root",
})
export class AssetService {
  private http = inject(HttpClient)
  private authService = inject(AuthService)

  private getApiUrl(): Observable<string> {
    return this.authService.currentUser$.pipe(
      filter((user) => !!user?.account_id),
      take(1), // Take first valid user and complete
      switchMap((user) => {
        return new Observable<string>((observer) => {
          observer.next(`${environment.apiUrl}/accounts/${user!.account_id}/assets`)
          observer.complete()
        })
      }),
    )
  }

  listAssets(params: HttpParams = new HttpParams()): Observable<PageResponse<FixedAsset>> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<PageResponse<FixedAsset>>(apiUrl, { params })),
    )
  }

  getAssets(entityId?: string, isActive?: boolean): Observable<FixedAsset[]> {
    let params = new HttpParams()
    if (entityId) {
      params = params.set("entityId", entityId)
    }
    if (isActive !== undefined) {
      params = params.set("is_active", isActive.toString())
    }
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<FixedAsset[]>(apiUrl, { params })),
    )
  }

  getAsset(id: string): Observable<FixedAsset> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<FixedAsset>(`${apiUrl}/${id}`)),
    )
  }

  createAsset(asset: FixedAssetCreate): Observable<FixedAsset> {
    return this.getApiUrl().pipe(switchMap((apiUrl) => this.http.post<FixedAsset>(apiUrl, asset)))
  }

  updateAsset(id: string, asset: FixedAssetUpdate): Observable<FixedAsset> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.put<FixedAsset>(`${apiUrl}/${id}`, asset)),
    )
  }

  deleteAsset(id: string): Observable<void> {
    return this.getApiUrl().pipe(switchMap((apiUrl) => this.http.delete<void>(`${apiUrl}/${id}`)))
  }

  getDepreciationHistory(id: string): Observable<DepreciationEntry[]> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) => this.http.get<DepreciationEntry[]>(`${apiUrl}/${id}/depreciation`)),
    )
  }

  calculateDepreciation(
    purchasePrice: number,
    salvageValue: number,
    usefulLifeMonths: number,
    method: string,
    purchaseDate: string,
  ): Observable<DepreciationCalculationResult> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) =>
        this.http.post<DepreciationCalculationResult>(`${apiUrl}/calculate-depreciation`, {
          purchasePrice,
          salvageValue,
          usefulLifeMonths,
          method,
          purchaseDate,
        }),
      ),
    )
  }

  postDepreciation(assetId: string, accountingDate: string): Observable<DepreciationEntry> {
    return this.getApiUrl().pipe(
      switchMap((apiUrl) =>
        this.http.post<DepreciationEntry>(`${apiUrl}/post-depreciation`, {
          assetId,
          accountingDate,
        }),
      ),
    )
  }
}
